// ============================================================================
// BackendOfflineBanner: the one status while QuoteMaker's server is not
// answering (outage lane, 2026-09-29).
// ----------------------------------------------------------------------------
// Shown while `apiStatus.disabled` (network-resilience's reachability flag).
// It replaced a red "Backend unreachable — changes won't save" strip whose
// promise was true then and is false now: a save the server cannot take is
// kept on this device (unsaved-project-edits) and sent when the server answers,
// and the reconnect loop probes /api/health by itself on a backoff. The save
// path no longer raises a second red banner or a toast per attempt, so this is
// the only status. Dismissible for the current outage, per the connectivity
// rule in CLAUDE.md. App owns visibility and the Try now handler.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Icons } from './Icon';
import { getBackendRecoveryState } from '../../services/network-resilience';
import { retryCountdownText, secondsUntil } from '../../utils/retry-countdown';

interface BackendOfflineBannerProps {
    /** The one-line result of the last Try now, or null when idle. */
    retryMessage: { tone: 'success' | 'error'; text: string } | null;
    /** True while a Try now check is in flight. */
    retrying: boolean;
    /** Checks the server now. */
    onRetry: () => void;
    /** Hides the banner for this outage. Omitted: no dismiss control. */
    onDismiss?: () => void;
    /** The open project has changes the server has not taken yet. */
    hasUnsavedWork?: boolean;
    /** Those changes are in the local database (a project never saved yet is only on screen). */
    workKeptOnDevice?: boolean;
}

export function BackendOfflineBanner({ retryMessage, retrying, onRetry, onDismiss, hasUnsavedWork = false, workKeptOnDevice = false }: BackendOfflineBannerProps) {
    const [recovery, setRecovery] = useState(() => ({ ...getBackendRecoveryState(), now: Date.now() }));

    useEffect(() => {
        const tick = () => setRecovery({ ...getBackendRecoveryState(), now: Date.now() });
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, []);

    const checking = retrying || recovery.probing;
    // The buttons carry their own class, not qm-inline-action-btn: that class
    // zeroes min-height with !important and took Try now under 44px on a phone.
    const countdown = checking
        ? 'Checking now.'
        : (recovery.nextProbeAt ? retryCountdownText(secondsUntil(recovery.nextProbeAt, recovery.now)) : '');

    return (
        <div
            role="status"
            aria-live="polite"
            data-qm-surface="server-status-banner"
            style={{
                background: '#fdf1e7',
                color: '#1c1917',
                padding: '8px 12px 8px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexShrink: 0,
                zIndex: 41,
                flexWrap: 'wrap',
            }}
        >
            <span
                aria-hidden="true"
                style={{ width: '8px', height: '8px', borderRadius: '999px', background: 'var(--qm-soft-attn-ink)', flexShrink: 0 }}
            />
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#1c1917', margin: 0 }}>
                    QuoteMaker is updating or can&apos;t reach its server.
                </p>
                <p style={{ fontSize: '13px', fontWeight: 400, color: '#44403c', margin: '2px 0 0' }}>
                    {!hasUnsavedWork
                        ? 'Your work is kept on this device.'
                        : workKeptOnDevice
                            ? 'Your changes are kept on this device and will save when it answers.'
                            : 'Your changes are on this screen and will save when it answers. Keep this tab open until then.'}
                    {' '}We&apos;ll reconnect by ourselves.
                </p>
                {retryMessage && (
                    <p style={{ fontSize: '13px', fontWeight: 500, margin: '2px 0 0', color: retryMessage.tone === 'success' ? '#166534' : 'var(--qm-soft-attn-ink)' }}>
                        {retryMessage.text}
                    </p>
                )}
            </div>
            {/* One group so a phone wraps it to a single row under the text. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto', flexShrink: 0 }}>
            {countdown ? (
                <span style={{ fontSize: '13px', color: '#6b645c', fontVariantNumeric: 'tabular-nums' }}>{countdown}</span>
            ) : null}
            <button
                type="button"
                className="qm-server-status-banner__button"
                onClick={onRetry}
                disabled={retrying}
                style={{
                    minHeight: '44px',
                    padding: '0 16px',
                    borderRadius: '10px',
                    border: 0,
                    background: '#ece7df',
                    color: '#1c1917',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: retrying ? 'wait' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexShrink: 0,
                }}
            >
                <Icons.RefreshCw size={13} aria-hidden="true" /> {retrying ? 'Checking…' : 'Try now'}
            </button>
            {onDismiss && (
                <button
                    type="button"
                    className="qm-server-status-banner__button"
                    onClick={onDismiss}
                    aria-label="Dismiss the server status"
                    style={{
                        minHeight: '44px',
                        minWidth: '44px',
                        border: 0,
                        borderRadius: '10px',
                        background: 'transparent',
                        color: '#44403c',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Icons.X size={16} aria-hidden="true" />
                </button>
            )}
            </div>
        </div>
    );
}
